import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { camelizeKeys } from "utils/camelizeKeys";
import { formatL0InfoLabel } from "utils/formatL0InfoDisplay";

import { Dialog, PrimaryButton, SecondaryButton, Stepper } from "widgets";
import PackageSubscription from "components/PackageSubscription/PackageSubscription";
import OrderSummary from "components/OrderSummary/OrderSummary";
import ConfirmOrder from "components/ConfirmOrder/ConfirmOrder";
import {
  useFetchPackagesAndSubscriptions,
  useFetchUser,
  usePunchOrder,
  useSelectedOrderDetails,
} from "store/backend/assets";
import {
  BUSINESS_VERTICALS,
  HARDWARE_PRODUCT_TYPE_ID,
  KH_VERTICAL_ID,
  NO_OF_PACKAGE_SUBSCRIPTION_IN_KH,
  DISCOUNT_TYPE,
  ZERO,
} from "constants/config";
import ReviewTransferOrder from "components/ReviewTransferOrder/ReviewTransferOrder";
import RequestTransferOrder from "components/RequestTransferOrder/RequestTransferOrder";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import PageNotFound from "components/PageNotFound/PageNotFound";
import { grantBatchPreferenceAccess } from "utils/batchPreferenceStorage";

import styles from "./PunchOrder.module.scss";

const PACKAGE_SUBSCRIPTION_SELECTION = 0;
const ADDRESS_FORM_AND_DISCOUNT_APPLICATION = 1;
const REVIEW_TRANSFER_ORDER = 2;

const STEPS_LABEL = [
  { label: "Select Packages" },
  { label: "Add billing info and discount" },
];

const buildNameFromNested = (nested) => {
  const fn = nested.firstName ?? nested.first_name;
  const ln = nested.lastName ?? nested.last_name;
  if (fn || ln) {
    return [fn, ln].filter(Boolean).join(" ").trim();
  }
  return (
    nested.name ??
    nested.fullName ??
    nested.full_name ??
    nested.label ??
    ""
  );
};

const getL0FromTransferOrder = (transferOrderDetails) => {
  if (!transferOrderDetails) {
    return { attributedTo: undefined, attributedToName: "", l0Info: null };
  }
  const t = transferOrderDetails;

  const l0InfoRaw = t.l0Info ?? t.l0_info;
  if (l0InfoRaw && typeof l0InfoRaw === "object" && !Array.isArray(l0InfoRaw)) {
    const l0Info = l0InfoRaw;
    const labelFromL0 = formatL0InfoLabel(l0Info);
    const attributedTo =
      l0Info.uamId ??
      l0Info.uam_id ??
      l0Info.id ??
      t.attributedTo ??
      t.attributed_to;
    const attributedToName =
      labelFromL0 ||
      buildNameFromNested(l0Info) ||
      (t.attributedToName ?? t.attributed_to_name ?? "");
    return {
      attributedTo,
      attributedToName: attributedToName || "",
      l0Info,
    };
  }

  const nested =
    t.attributedSalesPerson ||
    t.attributed_sales_person ||
    t.l0SalesPerson ||
    t.l0_sales_person ||
    t.attributedToDetails ||
    null;

  let attributedTo =
    t.attributedTo ??
    t.l0SalesPersonId ??
    t.attributed_to_uam_id ??
    t.attributed_to;

  let attributedToName =
    t.attributedToName ??
    t.l0SalesPersonName ??
    t.attributed_to_name ??
    "";

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    attributedTo =
      attributedTo ??
      nested.uamId ??
      nested.uam_id ??
      nested.id ??
      nested.crnId ??
      nested.crn_id;
    if (!attributedToName) {
      attributedToName = buildNameFromNested(nested);
    }
  }

  return {
    attributedTo,
    attributedToName: attributedToName || "",
    l0Info: null,
  };
};

/** True when the original order payload includes L0 (show read-only); else user must search. */
const isL0ProvidedByOrderData = (l0) => {
  if (!l0) return false;
  if (
    l0.l0Info &&
    typeof l0.l0Info === "object" &&
    !Array.isArray(l0.l0Info) &&
    Object.keys(l0.l0Info).length > 0
  ) {
    return true;
  }
  if (l0.attributedTo != null && l0.attributedTo !== "") {
    return true;
  }
  if (l0.attributedToName && String(l0.attributedToName).trim() !== "") {
    return true;
  }
  return false;
};

const SyncTransferOrderL0Fields = ({
  isOrderTransfer,
  orderInfoForTransfer,
  transferOrderDetails,
  setFieldValue,
}) => {
  useEffect(() => {
    if (!isOrderTransfer || !orderInfoForTransfer || !transferOrderDetails) {
      return;
    }
    const merged = {
      ...transferOrderDetails,
      ...camelizeKeys(orderInfoForTransfer),
    };
    const l0 = getL0FromTransferOrder(merged);
    const hasL0FromOrder = isL0ProvidedByOrderData(l0);
    setFieldValue("orderDetails.l0ProvidedByOrder", hasL0FromOrder);
    if (!hasL0FromOrder) {
      return;
    }
    setFieldValue("orderDetails.attributedTo", l0.attributedTo);
    setFieldValue("orderDetails.attributedToName", l0.attributedToName);
    setFieldValue("orderDetails.l0Info", l0.l0Info ?? null);
  }, [
    isOrderTransfer,
    orderInfoForTransfer,
    transferOrderDetails,
    setFieldValue,
  ]);
  return null;
};

const getInitialValues = (
  transferedPackageSubscriptions,
  transferedOrderVerticalAndTenantIds,
  selectedBusinessVerticalId,
  transferOrderDetails,
) => {
  const l0FromOriginalOrder = getL0FromTransferOrder(transferOrderDetails);
  return {
    orderDetails: {
      billingAddress: {
        hasMiddleEastCountry: false,
      },
      shippingAddress: undefined,
      discount: {},
      hasHardware: false,
      serviceStartDate: "",
      attributedTo: l0FromOriginalOrder.attributedTo,
      attributedToName: l0FromOriginalOrder.attributedToName,
      l0Info: l0FromOriginalOrder.l0Info ?? null,
      l0ProvidedByOrder: isL0ProvidedByOrderData(l0FromOriginalOrder),
    },
    priceDetailsAfterDiscount: {},
    selectedPackageSubscriptions: transferedPackageSubscriptions || [],
    currentStep: PACKAGE_SUBSCRIPTION_SELECTION,
    filters: {
      selectedGrade: [],
      selectedTargetExam: [],
      selectedVerticalId:
        transferedOrderVerticalAndTenantIds.verticalId ||
        Number(selectedBusinessVerticalId),
      selectedTenantId: transferedOrderVerticalAndTenantIds.tenantId || null,
      selectedOfficeLocationId:
        transferedOrderVerticalAndTenantIds.officeLocationId || null,
    },
    searchTerm: "",
    useSameAddress: false,
    openDialog: false,
  };
};

const buildDiscountPayload = (discountDetails) => {
  if (!discountDetails) {
    return null;
  }

  if (discountDetails.discount_type === DISCOUNT_TYPE.DYNAMIC) {
    return {
      discount_value: discountDetails.discount_value,
      is_percentage: discountDetails.is_percentage,
      discount_type: discountDetails.discount_type,
    };
  }

  if (discountDetails.discount_type === DISCOUNT_TYPE.COUPON_CODE) {
    return {
      discount_type: discountDetails.discount_type,
      coupon_code: discountDetails.coupon_code,
      customer_id: discountDetails.customer_id,
      ...(discountDetails.discount_value
        ? {
            discount_value: discountDetails.discount_value,
            is_percentage: discountDetails.is_percentage || false,
          }
        : {}),
    };
  }

  return null;
};

const buildSkuPayload = (
  selectedPackageSubscriptions,
  priceDetailsAfterDiscount,
  selectedVerticalId
) => {
  const discountDetails =
    priceDetailsAfterDiscount.subscription_discount_details || [];

  return selectedPackageSubscriptions.reduce((acc, selectedPackage) => {
    const matchingDiscount = discountDetails.find(
      (discount) => discount.subscription_id === selectedPackage.id
    );

    const subscriptionAmount = matchingDiscount
      ? selectedPackage.mrp - matchingDiscount.discount_amount
      : selectedPackage.mrp;

    const books_price = matchingDiscount
      ? matchingDiscount.books_price_after_discount
      : selectedPackage.booksPrice || 0;

    const existingPackage = acc.find(
      (result) => result.id === selectedPackage.packageId
    );

    if (existingPackage) {
      return acc.map((result) =>
        result.id === selectedPackage.packageId
          ? {
              ...result,
              vertical_id: selectedVerticalId,
              subscriptions: [
                ...result.subscriptions,
                {
                  id: selectedPackage.id,
                  listed_amount: reduceToFixedDecimals(selectedPackage.mrp),
                  subscription_amount:
                    reduceToFixedDecimals(subscriptionAmount),
                  books_price: reduceToFixedDecimals(books_price),
                },
              ],
            }
          : result
      );
    } else {
      return [
        ...acc,
        {
          id: selectedPackage.packageId,
          vertical_id: selectedVerticalId,
          subscriptions: [
            {
              id: selectedPackage.id,
              listed_amount: reduceToFixedDecimals(selectedPackage.mrp),
              subscription_amount: reduceToFixedDecimals(subscriptionAmount),
              books_price: reduceToFixedDecimals(books_price),
            },
          ],
        },
      ];
    }
  }, []);
};

const totalMrp = (selectedPackageSubscriptions) =>
  selectedPackageSubscriptions.reduce((total, packageSubscription) => {
    return total + (packageSubscription.mrp || 0);
  }, 0);

const buildPayload = (values, newUamId, sales_person_id) => {
  const {
    orderDetails,
    selectedPackageSubscriptions,
    priceDetailsAfterDiscount,
    filters,
  } = values;

  const listedOrderAmount =
    priceDetailsAfterDiscount.listedOrderAmount ||
    totalMrp(selectedPackageSubscriptions);

  const orderAmount =
    priceDetailsAfterDiscount.orderAmount ??
    totalMrp(selectedPackageSubscriptions);

  const discountPayload = buildDiscountPayload(orderDetails.discount);

  const discount =
    !!discountPayload && !!discountPayload.discount_type
      ? discountPayload
      : null;

  const billing_address = {
    district_id: orderDetails.billingAddress.districtId,
    street: orderDetails.billingAddress.street
      ? orderDetails.billingAddress.street.trim()
      : "",
    country_id: 1,
    postal_code_id: orderDetails.billingAddress.pincodeId,
    city: orderDetails.billingAddress.city.trim(),
    state_id: orderDetails.billingAddress.stateId,
    address_line_1: orderDetails.billingAddress.addressLine1.trim(),
    address_line_2: orderDetails.billingAddress.addressLine2
      ? orderDetails.billingAddress.addressLine2.trim()
      : "",
    recipient_name: orderDetails.billingAddress.recipientName.trim(),
    recipient_phone_number: orderDetails.billingAddress.recipientPhoneNumber,
    email: orderDetails.billingAddress.recipientEmail.trim(),
    parent_name: orderDetails.billingAddress.parentName.trim(),
  };

  const shipping_address = values.useSameAddress
    ? billing_address
    : orderDetails.shippingAddress
    ? {
        district_id: orderDetails.shippingAddress.districtId,
        street: orderDetails.billingAddress.street
          ? orderDetails.billingAddress.street.trim()
          : "",
        country_id: 1,
        postal_code_id: orderDetails.shippingAddress.pincodeId,
        city: orderDetails.shippingAddress.city.trim(),
        state_id: orderDetails.shippingAddress.stateId,
        address_line_1: orderDetails.billingAddress.addressLine1.trim(),
        address_line_2: orderDetails.billingAddress.addressLine2
          ? orderDetails.billingAddress.addressLine2.trim()
          : "",
        recipient_name: orderDetails.shippingAddress.recipientName.trim(),
        recipient_phone_number:
          orderDetails.shippingAddress.recipientPhoneNumber,
        email: orderDetails.shippingAddress.recipientEmail.trim(),
        parent_name: orderDetails.shippingAddress.parentName.trim(),
      }
    : null;

  return {
    listed_order_amount: reduceToFixedDecimals(listedOrderAmount),
    order_amount: reduceToFixedDecimals(orderAmount),
    business_unit_id: filters.selectedVerticalId,
    service_start_date: orderDetails.serviceStartDate,
    order_punched_by: sales_person_id,
    attributed_to: orderDetails.attributedTo,
    office_location_id: filters.selectedOfficeLocationId,
    tenant_id: filters.selectedTenantId,
    platform_id: 1,
    currency_id: 1,
    skus: buildSkuPayload(
      selectedPackageSubscriptions,
      priceDetailsAfterDiscount,
      filters.selectedVerticalId
    ),
    customer_id: newUamId,
    billing_address: billing_address,
    shipping_address: shipping_address,
    discount: discount,
  };
};

const advanceToNextStep = (
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
) => {
  setFieldValue("currentStep", currentStep + 1);
  const hasHardware = selectedPackageSubscriptions.some((pkg) =>
    pkg.componentProductTypeIds.includes(HARDWARE_PRODUCT_TYPE_ID),
  );
  setFieldValue("orderDetails.hasHardware", hasHardware);
};

const handleClickNextButton = async ({
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
  isOrderTransfer,
  openSnackbar,
  refetchEnrichedSubscriptions,
}) => {
  if (currentStep === PACKAGE_SUBSCRIPTION_SELECTION && isOrderTransfer) {
    const { data, error } = await refetchEnrichedSubscriptions();
    if (error) {
      openSnackbar(
        error?.message ||
          "Failed to fetch package book prices. Please try again.",
        "error",
      );
      return;
    }
    setFieldValue("selectedPackageSubscriptions", data);
    advanceToNextStep(currentStep, setFieldValue, data);
    return;
  }

  advanceToNextStep(currentStep, setFieldValue, selectedPackageSubscriptions);
};

const createAddressSchema = (selectedVerticalId, tenantStateId) => {
  const baseAddressSchema = z.object({
    recipientName: z
      .string()
      .min(1, "Recipient Name is required")
      .max(50, "Recipient Name cannot be more than 50 characters"),
    parentName: z
      .string()
      .min(1, "Parent Name is required")
      .max(50, "Parent Name cannot be more than 50 characters"),
    recipientPhoneNumber: z
      .string()
      .min(10, "Phone number must be exactly 10 digits")
      .max(10, "Phone number must be exactly 10 digits")
      .regex(/^[6-9]\d{9}$/, "Invalid phone number"),
    recipientEmail: z
      .string()
      .email("Invalid email address")
      .min(5, "Email must be at least 5 characters long")
      .max(254, "Email is too long"),
    addressLine1: z
      .string()
      .trim()
      .min(25, "Please enter complete address line of the customer")
      .max(100, "Address Line 1 cannot be more than 100 characters"),
    city: z.string().min(1, "City is required"),
    pincode: z
      .string()
      .max(6, "Pincode must be at most 6 digits")
      .regex(/^\d{6}$/, "Pincode must be 6 digits"),
    district: z.string().min(1, "District is required"),
    state: z.string().min(1, "State is required"),
    stateId: z.union([z.number(), z.string(), z.null()]).optional(),
  });

  if (
    Number(selectedVerticalId) === Number(KH_VERTICAL_ID) &&
    tenantStateId !== null &&
    tenantStateId !== undefined
  ) {
    return baseAddressSchema.refine(
      (data) => {
        if (data.stateId === null || data.stateId === undefined) {
          return false;
        }
        return Number(data.stateId) === Number(tenantStateId);
      },
      {
        message: "Address state must match the tenant state",
        path: ["stateId"],
      }
    );
  }

  return baseAddressSchema;
};

const punchOrderAfterSetUamId = (
  values,
  navigate,
  punchOrder,
  sales_person_id,
  openSnackbar,
  uam_id
) => {
  const parsedUamId = uam_id === "null" ? null : uam_id;

  if (!Boolean(parsedUamId)) {
    openSnackbar(
      "An unexpected error occurred while punching the order. Please try again.",
      "error"
    );
  } else {
    const payload = buildPayload(values, uam_id, sales_person_id);

    punchOrder(payload, {
      onSuccess: (orderId) => {
        grantBatchPreferenceAccess(orderId);
        const batchPreferenceQuery = parsedUamId
          ? `order_id=${orderId}&uam_id=${encodeURIComponent(parsedUamId)}`
          : `order_id=${orderId}`;
        navigate(`/batchPreference?${batchPreferenceQuery}`, {
          replace: true,
        });
      },
      onError: (error) => {
        openSnackbar(
          error?.message ||
            "An unexpected error occurred while processing the order. Please try again.",
          "error"
        );
      },
    });
  }
};

const getPackageSubscriptions = (transferedPackages, verticalId) => {
  if (!transferedPackages || transferedPackages.length === 0) {
    return [];
  }
  return transferedPackages.flatMap((pkg) => {
    if (!pkg.subscriptions || pkg.subscriptions.length === 0) {
      return [];
    }

    return pkg.subscriptions.map((subscription) => {
      return {
        id: subscription.id,
        booksPrice: subscription.bookPrice || 0,
        packageId: pkg.id,
        verticalId: verticalId,
        displayName: pkg.displayName,
        planName: subscription.planCategory.name,
        planValidity: pkg.validityDate,
        mrp: subscription.mrp,
        componentProductTypeIds:
          subscription.components?.map(
            (component) => component.componentProduct?.componentProductTypeId
          ) || [],
      };
    });
  });
};

const getVerticalAndTenantIdsOfTransferedOrder = (transferOrderDetails) => {
  if (!transferOrderDetails) {
    return {};
  }
  return {
    verticalId: transferOrderDetails.businessUnitId,
    tenantId: transferOrderDetails.tenantId,
    officeLocationId: transferOrderDetails.officeLocationId,
  };
};

const handleDialogOpen =
  (setFieldValue, content, size = {}) =>
  () => {
    setFieldValue("openDialog", { content, size });
  };

const isSaveAndContinueButtonDisabled = (
  selectedPackageSubscriptions,
  filters,
  currentStep,
  isValid,
  isOrderTransfer,
  serviceStartDate,
  attributedTo
) => {
  if (selectedPackageSubscriptions.length === 0) return true;

  if (
    filters.selectedVerticalId === BUSINESS_VERTICALS.KH &&
    selectedPackageSubscriptions.length !== NO_OF_PACKAGE_SUBSCRIPTION_IN_KH
  )
    return true;

  if (!filters.selectedVerticalId) return true;
  if (isOrderTransfer && !filters.selectedTenantId) return true;
  const onAddressStep = currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION;

  if (
    onAddressStep &&
    isOrderTransfer &&
    (!isValid || !serviceStartDate || !attributedTo)
  )
    return true;

  if (onAddressStep && !isOrderTransfer && (!isValid || !attributedTo))
    return true;
  if (
    filters.selectedVerticalId === KH_VERTICAL_ID &&
    selectedPackageSubscriptions.length > 1
  )
    return true;
  return false;
};

const PunchOrderStepActions = ({
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
  filters,
  isValid,
  isOrderTransfer,
  isSubmitting,
  orderDetails,
  openSnackbar,
  values,
  navigate,
  punchOrder,
  sales_person_id,
  uam_id,
  isPunchOrderLoading,
  error,
  isError,
  transferOrderDetails,
  selectedBusinessVerticalId,
}) => {
  const {
    refetch: refetchEnrichedSubscriptions,
    isFetching: isEnrichingBookPrices,
  } = useFetchPackagesAndSubscriptions(selectedPackageSubscriptions, {
    enabled: false,
  });

  return (
    <div className={styles.lineItemWrapper}>
      {currentStep > PACKAGE_SUBSCRIPTION_SELECTION && (
        <SecondaryButton
          onClick={() => setFieldValue("currentStep", currentStep - 1)}
        >
          Go Back
        </SecondaryButton>
      )}
      {currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION &&
      !isOrderTransfer ? (
        <PrimaryButton
          loading={isSubmitting}
          onClick={handleDialogOpen(
            setFieldValue,
            <ConfirmOrder
              onConfirm={() =>
                punchOrderAfterSetUamId(
                  values,
                  navigate,
                  punchOrder,
                  sales_person_id,
                  openSnackbar,
                  uam_id,
                )
              }
              onCancel={() => setFieldValue("openDialog", false)}
              isPending={isPunchOrderLoading}
              error={error}
              isError={isError}
            />,
            { width: "25rem", height: "10rem" },
          )}
          disabled={
            !orderDetails.serviceStartDate ||
            !isValid ||
            !orderDetails.attributedTo
          }
        >
          Confirm Order
        </PrimaryButton>
      ) : (
        currentStep !== REVIEW_TRANSFER_ORDER && (
          <PrimaryButton
            loading={isSubmitting || isEnrichingBookPrices}
            disabled={
              isEnrichingBookPrices ||
              isSaveAndContinueButtonDisabled(
                selectedPackageSubscriptions,
                filters,
                currentStep,
                isValid,
                isOrderTransfer,
                orderDetails.serviceStartDate,
                orderDetails.attributedTo,
              )
            }
            onClick={() =>
              handleClickNextButton({
                currentStep,
                setFieldValue,
                selectedPackageSubscriptions,
                isOrderTransfer,
                openSnackbar,
                refetchEnrichedSubscriptions,
              })
            }
          >
            Save & Continue
          </PrimaryButton>
        )
      )}
      {currentStep === REVIEW_TRANSFER_ORDER ? (
        <PrimaryButton
          onClick={handleDialogOpen(
            setFieldValue,
            <RequestTransferOrder
              values={values}
              uam_id={uam_id}
              sales_person_id={sales_person_id}
              orderId={transferOrderDetails.orderId}
              selectedBusinessVerticalId={selectedBusinessVerticalId}
              onCancel={() => setFieldValue("openDialog", false)}
            />,
            { width: "25rem", height: "10rem" },
          )}
        >
          Confirm Modification
        </PrimaryButton>
      ) : null}
    </div>
  );
};

const getPreviousOrderAcademicYear = (transferOrderDetails) => {
  const { businessUnitId, packages } = transferOrderDetails;

  if (businessUnitId !== KH_VERTICAL_ID) {
    return null;
  }
  if (!Array.isArray(packages) || packages.length === ZERO) {
    return null;
  }
  return packages[ZERO].academicYear ?? null;
};

const PunchOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const uam_id = searchParams.get("uamId");
  const selectedBusinessVerticalId = searchParams.get("verticalId");
  const [sales_person_id] = useState(localStorage.getItem("sales_person_id"));
  const location = useLocation();
  const transferOrderDetails = location.state?.transferOrderDetails
    ? camelizeKeys(location.state.transferOrderDetails)
    : null;

  const isOrderTransfer =
    transferOrderDetails && Object.keys(transferOrderDetails).length > 0;
  
  const transferedL0Details = isOrderTransfer
  ? {
      attributedTo: transferOrderDetails?.l0Info?.uamId,
      attributedToName: transferOrderDetails?.l0Info?.email,
    }
  : {};

  const stepsLabels = isOrderTransfer
    ? [...STEPS_LABEL, { label: "Review Transfer Order" }]
    : STEPS_LABEL;

  const isInvalidParam =
    !uam_id ||
    !selectedBusinessVerticalId ||
    uam_id === "null" ||
    uam_id === "undefined" ||
    selectedBusinessVerticalId === "null" ||
    selectedBusinessVerticalId === "undefined";

  const {
    mutate: punchOrder,
    isPending: isPunchOrderLoading,
    isError,
    error,
  } = usePunchOrder();

  const {
    isPending: isSelectedSelectedUserDetailsLoading,
    data: selectedUserDetails,
    isError: isErrorSelectedSelectedUserDetails,
    error: errorMessageOnSelectedSelectedUserDetails,
  } = useFetchUser(uam_id, selectedBusinessVerticalId);

  const transferOrderIdForQuery =
    isOrderTransfer && transferOrderDetails?.orderId
      ? transferOrderDetails.orderId
      : null;

  const { data: orderInfoForTransfer } =
    useSelectedOrderDetails(transferOrderIdForQuery);

  const transferOrderDetailsForL0 = useMemo(() => {
    if (!isOrderTransfer || !transferOrderDetails) {
      return null;
    }
    if (orderInfoForTransfer) {
      return {
        ...transferOrderDetails,
        ...camelizeKeys(orderInfoForTransfer),
      };
    }
    return transferOrderDetails;
  }, [isOrderTransfer, transferOrderDetails, orderInfoForTransfer]);

  if (isInvalidParam) {
    return <PageNotFound />;
  }
  if (isSelectedSelectedUserDetailsLoading) {
    return <FullPageLoader />;
  }

  if (isErrorSelectedSelectedUserDetails) {
    return (
      <ErrorFallback
        message={errorMessageOnSelectedSelectedUserDetails.message}
      />
    );
  }

  const transferedPackageSubscriptions = isOrderTransfer
    ? getPackageSubscriptions(
        transferOrderDetails?.packages,
        transferOrderDetails?.businessUnitId
      )
    : [];

  const previousOrderAcademicYear = isOrderTransfer
    ? getPreviousOrderAcademicYear(transferOrderDetails)
    : null;

  const transferedOrderVerticalAndTenantIds = isOrderTransfer
    ? getVerticalAndTenantIdsOfTransferedOrder(transferOrderDetails)
    : {
        verticalId: null,
        tenantId: null,
        officeLocationId: null,
      };

  const initialValues = getInitialValues(
    transferedPackageSubscriptions,
    transferedOrderVerticalAndTenantIds,
    selectedBusinessVerticalId,
    isOrderTransfer ? transferOrderDetailsForL0 : null,
  );

  const selectedVerticalId = initialValues.filters?.selectedVerticalId;
  const tenantStateId = initialValues.filters?.selectedOfficeLocationId;
  const addressSchema = createAddressSchema(selectedVerticalId, tenantStateId);

  const orderDetailsHasHardware = z
    .object({
      billingAddress: addressSchema,
      shippingAddress: addressSchema,
      hasHardware: z.literal(true),
    })
    .passthrough();

  const orderDetailsHasNoHardware = z
    .object({
      billingAddress: addressSchema,
      shippingAddress: z.optional(addressSchema),
      hasHardware: z.literal(false),
    })
    .passthrough();

  const baseFormValidationSchema = z.object({
    orderDetails: z.discriminatedUnion("hasHardware", [
      orderDetailsHasHardware,
      orderDetailsHasNoHardware,
    ]),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(baseFormValidationSchema)}
      validateOnChange={true}
      validateOnBlur={true}
      validate={(values) => {
        const currentVerticalId = values.filters?.selectedVerticalId;
        const currentTenantStateId = values.filters?.selectedOfficeLocationId;
        const errors = {};

        if (
          currentVerticalId &&
          Number(currentVerticalId) === Number(KH_VERTICAL_ID) &&
          currentTenantStateId !== null &&
          currentTenantStateId !== undefined
        ) {
          const billingAddress = values.orderDetails?.billingAddress;
          if (billingAddress) {
            const billingStateId = billingAddress.stateId;
            if (
              billingStateId === null ||
              billingStateId === undefined ||
              String(billingStateId).trim() === "" ||
              Number(billingStateId) !== Number(currentTenantStateId)
            ) {
              if (!errors.orderDetails) errors.orderDetails = {};
              if (!errors.orderDetails.billingAddress)
                errors.orderDetails.billingAddress = {};
              errors.orderDetails.billingAddress.stateId =
                "Address state must match the tenant state";
            }
          }

          if (
            values.orderDetails?.hasHardware &&
            values.orderDetails?.shippingAddress
          ) {
            const shippingStateId = values.orderDetails.shippingAddress.stateId;
            if (
              shippingStateId === null ||
              shippingStateId === undefined ||
              String(shippingStateId).trim() === "" ||
              Number(shippingStateId) !== Number(currentTenantStateId)
            ) {
              if (!errors.orderDetails) errors.orderDetails = {};
              if (!errors.orderDetails.shippingAddress)
                errors.orderDetails.shippingAddress = {};
              errors.orderDetails.shippingAddress.stateId =
                "Address state must match the tenant state";
            }
          }
        }

        return Object.keys(errors).length > 0 ? errors : undefined;
      }}
    >
      {(formikProps) => {
        const {
          values,
          errors,
          setFieldValue,
          isValid,
          isSubmitting,
          handleBlur,
          validateForm,
        } = formikProps;

        const {
          orderDetails,
          currentStep,
          selectedPackageSubscriptions,
          filters,
          searchTerm,
          useSameAddress,
          openDialog,
          priceDetailsAfterDiscount,
        } = values;

        const modifiedOrderAmount =
          priceDetailsAfterDiscount?.orderAmount !== undefined &&
          priceDetailsAfterDiscount?.orderAmount !== null
            ? priceDetailsAfterDiscount.orderAmount
            : totalMrp(selectedPackageSubscriptions);

        return (
          <div className={styles.root}>
            <SyncTransferOrderL0Fields
              isOrderTransfer={isOrderTransfer}
              orderInfoForTransfer={orderInfoForTransfer}
              transferOrderDetails={transferOrderDetails}
              setFieldValue={setFieldValue}
            />
            <Stepper activeStep={currentStep}>
              <Stepper.Steps steps={stepsLabels} activeStep={currentStep} />
            </Stepper>
            <div className={styles.stepContentContainer}>
              {currentStep === PACKAGE_SUBSCRIPTION_SELECTION ? (
                <PackageSubscription
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  filters={filters}
                  searchTerm={searchTerm}
                  isOrderTransfer={isOrderTransfer}
                  previousOrderTenantId={
                    isOrderTransfer ? transferOrderDetails?.tenantId : null
                  }
                  previousOrderAcademicYear={previousOrderAcademicYear}
                  onPackagesSelected={(packages) =>
                    setFieldValue("selectedPackageSubscriptions", packages)
                  }
                  onFilterChange={(filterType, value) => {
                    setFieldValue(`filters.${filterType}`, value);
                  }}
                  onSearchTermChange={(value) => {
                    setFieldValue("searchTerm", value);
                  }}
                  handleChangePackageSubscriptionSelection={(packages) =>
                    setFieldValue("selectedPackageSubscriptions", packages)
                  }
                />
              ) : null}
            </div>
            <div className={styles.stepAddressFormContentContainer}>
              {currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION ? (
                <OrderSummary
                  orderDetails={orderDetails}
                  selectedVerticalId={filters.selectedVerticalId}
                  tenantStateId={filters.selectedOfficeLocationId}
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  setFieldValue={setFieldValue}
                  useSameAddress={useSameAddress}
                  isOrderTransfer={isOrderTransfer}
                  errors={errors}
                  handleBlur={handleBlur}
                  validateForm={validateForm}
                />
              ) : null}
              {isOrderTransfer && currentStep === REVIEW_TRANSFER_ORDER && (
                <ReviewTransferOrder
                  transferOrderDetails={transferOrderDetails}
                  billingAddress={orderDetails.billingAddress}
                  shippingAddress={orderDetails.shippingAddress}
                  useSameAddress={useSameAddress}
                  hasHardware={orderDetails.hasHardware}
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  discountDetails={orderDetails.discount}
                  newOrderAmount={modifiedOrderAmount}
                />
              )}
            </div>
            <PunchOrderStepActions
              currentStep={currentStep}
              setFieldValue={setFieldValue}
              selectedPackageSubscriptions={selectedPackageSubscriptions}
              filters={filters}
              isValid={isValid}
              isOrderTransfer={isOrderTransfer}
              isSubmitting={isSubmitting}
              orderDetails={orderDetails}
              openSnackbar={openSnackbar}
              values={values}
              navigate={navigate}
              punchOrder={punchOrder}
              sales_person_id={sales_person_id}
              uam_id={uam_id}
              isPunchOrderLoading={isPunchOrderLoading}
              error={error}
              isError={isError}
              transferOrderDetails={transferOrderDetails}
              selectedBusinessVerticalId={selectedBusinessVerticalId}
            />
            <Dialog
              open={!!openDialog}
              handleClose={() => setFieldValue("openDialog", false)}
              width={openDialog?.size?.width}
              height={openDialog?.size?.height}
            >
              {openDialog?.content}
            </Dialog>
          </div>
        );
      }}
    </Formik>
  );
};

export default PunchOrder;
